import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, ArrowRight, CheckCircle, Shield, Lock, Bot } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getImageUrl } from '@/lib/utils';
import StickyNavigation from "@/components/layout/StickyNavigation";
import TenantFooter from "@/components/layout/TenantFooter";
import { getPageTitle } from "@/utils/PageUtils";

type Licence = "mainland" | "free_zone" | "difc_adgm" | "other_offshore";
type Cover = "employee-medical" | "workmens-comp" | "public-liability" | "property-all-risks" | "cyber-protection" | "professional-indemnity";

interface Question {
  id: string;
  question: string;
  options: Array<{
    value: string;
    label: string;
    points?: Record<string, number>;
  }>;
}

const questions: Question[] = [
  {
    id: "licence_location",
    question: "Where is your business licensed?",
    options: [
      { value: "mainland", label: "UAE Mainland" },
      { value: "free_zone", label: "Free Zone" },
      { value: "difc_adgm", label: "DIFC/ADGM" },
      { value: "other_offshore", label: "Other Offshore" }
    ]
  },
  {
    id: "physical-property",
    question: "Do you own or lease the premises?",
    options: [
      { value: "own", label: "Yes, I own business property", points: { "property-all-risks": 3 } },
      { value: "lease", label: "Yes, I lease business property", points: { "property-all-risks": 2 } },
      { value: "none", label: "No physical property", points: {} }
    ]
  },
  {
    id: "client-visits",
    question: "Do clients visit your premises or could you damage third-party property?",
    options: [
      { value: "high", label: "Yes, high client interaction or third-party risk", points: { public_liability: 3 } },
      { value: "medium", label: "Some client visits or moderate risk", points: { public_liability: 2 } },
      { value: "low", label: "Minimal client interaction", points: { public_liability: 1 } },
      { value: "none", label: "No client visits or third-party risk", points: {} }
    ]
  },
  {
    id: "cyber-concerns",
    question: "Are you worried about cyber attacks?",
    options: [
      { value: "high", label: "Very concerned - we handle sensitive data", points: { cyber: 3 } },
      { value: "medium", label: "Somewhat concerned", points: { cyber: 2 } },
      { value: "low", label: "Not very concerned", points: { cyber: 1 } },
      { value: "none", label: "Not concerned", points: {} }
    ]
  },
  {
    id: "professional-advice",
    question: "Do you give professional, design, legal or consulting advice?",
    options: [
      { value: "core", label: "Yes, it's core to our business", points: { "professional-indemnity": 3 } },
      { value: "sometimes", label: "Sometimes, as part of our services", points: { "professional-indemnity": 2 } },
      { value: "minimal", label: "Minimal advisory services", points: { "professional-indemnity": 1 } },
      { value: "none", label: "No professional advice provided", points: {} }
    ]
  }
];

const coverLabels = {
  "employee-medical": "Employee Medical Insurance",
  "workmens-comp": "Workmen's Compensation",
  "public-liability": "Public Liability",
  "property-all-risks": "Property All Risks",
  "cyber-protection": "Cyber Insurance",
  "professional-indemnity": "Professional Indemnity"
};

function getMandatoryCovers(licence: Licence, leasesProperty: boolean): Cover[] {
  const mandatory: Cover[] = ["employee-medical"];

  if (licence === "free_zone") {
    mandatory.push("workmens-comp", "public-liability");
    if (leasesProperty) mandatory.push("property-all-risks");
  }

  return Array.from(new Set(mandatory));
}

const DecisionTree = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isComplete, setIsComplete] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [selectedProducts, setSelectedProducts] = useState<Record<string, boolean>>({});
  const [mandatoryCovers, setMandatoryCovers] = useState<Cover[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;

  const handleAnswer = (questionId: string, answerValue: string) => {
    const newAnswers = { ...answers, [questionId]: answerValue };
    setAnswers(newAnswers);

    // Calculate mandatory covers if we have licence and property info
    if (questionId === "licence_location" || questionId === "physical-property") {
      const licence = (questionId === "licence_location" ? answerValue : newAnswers.licence_location) as Licence;
      const propertyAnswer = questionId === "physical-property" ? answerValue : newAnswers["physical-property"];

      if (licence && propertyAnswer !== undefined) {
        const leasesProperty = propertyAnswer === "lease" || propertyAnswer === "own";
        const mandatory = getMandatoryCovers(licence, leasesProperty);
        setMandatoryCovers(mandatory);
      }
    }

    // Calculate scores for optional covers
    const question = questions[currentQuestion];
    const selectedOption = question.options.find(opt => opt.value === answerValue);

    if (selectedOption && selectedOption.points) {
      const newScores = { ...scores };
      Object.entries(selectedOption.points).forEach(([type, points]) => {
        newScores[type] = (newScores[type] || 0) + points;
      });
      setScores(newScores);
    }

    // Move to next question or complete
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setIsComplete(true);
    }
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const getRecommendedOptionalCovers = useCallback(() => {
    const optionalCovers: Cover[] = ["property-all-risks", "public-liability", "cyber-protection", "professional-indemnity"];

    return optionalCovers
      .filter(cover => !mandatoryCovers.includes(cover))
      .map(cover => ({
        type: cover,
        name: coverLabels[cover],
        score: scores[cover] || 0,
        priority: (scores[cover] || 0) >= 2 ? "Recommended" : "Optional"
      }))
      .filter(cover => cover.score > 0);
  }, [mandatoryCovers, scores]);

  const handleOptionalCoverToggle = (coverType: string, enabled: boolean) => {
    setSelectedProducts(prev => ({
      ...prev,
      [coverType]: enabled
    }));
  };

  const handleNext = () => {
    const allSelectedCovers = [
      ...mandatoryCovers,
      ...Object.entries(selectedProducts).filter(([_, selected]) => selected).map(([type, _]) => type)
    ];

    navigate(tLink('/confirmation'), {
      state: {
        selectedProducts: Array.from(new Set(allSelectedCovers)),
        mandatoryCovers,
        optionalCovers: getRecommendedOptionalCovers()
      }
    });
  };

  const pageTitle = "Decision Matrix";

  useEffect(() => {
    if (isComplete) {
      const initialSelection: Record<string, boolean> = {};
      getRecommendedOptionalCovers().forEach(cover => {
        initialSelection[cover.type] = false;
      });
      setSelectedProducts(initialSelection);
    }
  }, [isComplete, mandatoryCovers, scores, getRecommendedOptionalCovers]);

  // Handle navigation state from Confirmation page
  useEffect(() => {
    const state = location.state as { showAssessmentComplete?: boolean; mandatoryCovers?: string[]; optionalCovers?: string[] } | null;
    if (state?.showAssessmentComplete) {
      setIsComplete(true);
      if (state.mandatoryCovers) {
        setMandatoryCovers(state.mandatoryCovers);
      }
      if (state.selectedProducts) {
        const productSelection: Record<string, boolean> = {};
        state.selectedProducts.forEach((product: string) => {
          productSelection[product] = true;
        });
        setSelectedProducts(productSelection);
      }
    }
  }, [location.state]);

  const progress = ((currentQuestion + (isComplete ? 1 : 0)) / questions.length) * 100;

  if (isComplete) {
    const recommendedOptionalCovers = getRecommendedOptionalCovers();

    return (
      <div className="min-h-screen bg-gray-50">
        <StickyNavigation />
        <div className="py-4 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <button
                onClick={goBack}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span>Back</span>
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Assessment Complete
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Based on your licence type and business needs, we've identified your mandatory covers and recommended additional protection.
              </p>
            </div>

            {mandatoryCovers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">UAE Mandatory Coverage</h2>
                <div className="space-y-3">
                  {mandatoryCovers.map((coverType) => (
                    <Card key={coverType} className="border border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                              <Lock className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{coverLabels[coverType]}</h3>
                              <Badge variant="secondary" className="text-xs bg-red-100 text-red-800">
                                UAE Mandatory
                              </Badge>
                            </div>
                          </div>
                          <Checkbox checked={true} disabled={true} className="opacity-50" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {recommendedOptionalCovers.length > 0 && (
              <div className="mb-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">YallaCompare would also recommend you take these</h2>
                <div className="space-y-3">
                  {recommendedOptionalCovers.map((cover) => (
                    <Card key={cover.type} className="border border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Shield className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{cover.name}</h3>
                              <Badge
                                variant={cover.priority === "Recommended" ? "default" : "secondary"}
                                className={cover.priority === "Recommended" ? "bg-orange-100 text-orange-800" : ""}
                              >
                                {cover.priority}
                              </Badge>
                            </div>
                          </div>
                          <Switch
                            checked={selectedProducts[cover.type] || false}
                            onCheckedChange={(checked) => handleOptionalCoverToggle(cover.type, checked)}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center mb-8">
              <Button
                size="lg"
                onClick={handleNext}
                className="min-w-32"
              >
                Continue to Quote
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <Card className="border border-gray-200">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Ready to Get Protected?
                </h3>
                <p className="text-gray-600 mb-6">
                  Get personalized quotes for all your selected insurance products.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button size="lg" onClick={handleNext}>
                    Get Combined Quote
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(tLink('/business-insurance-guide'))}
                  >
                    Learn More About Business Insurance
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setCurrentQuestion(0);
                      setAnswers({});
                      setScores({});
                      setIsComplete(false);
                      setSelectedProducts({});
                      setMandatoryCovers([]);
                    }}
                  >
                    Retake Assessment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];

  useEffect(() => {
    document.title = getPageTitle(pageTitle);
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StickyNavigation />
      <div className="flex-1 flex flex-col">
        <div className="py-8 px-4 flex-1">
          <div className="max-w-2xl mx-auto h-full flex flex-col">
            <div className="mb-6">
              <Link
                to={tLink('/')}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span>Back to Home</span>
              </Link>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-center justify-center mx-auto mb-4 h-24 w-24 rounded-2xl bg-blue-100">
                <Bot className="h-12 w-12 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Let's figure out what you need
              </h1>
              <p className="text-base text-gray-600">
                {currentQuestion === 0
                  ? "First, let's identify your mandatory UAE insurance requirements."
                  : "Answer a few questions to find the right additional protection for your business."
                }
              </p>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-gray-600">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
                <span className="text-sm text-gray-600">
                  {Math.round(progress)}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <Card className="mb-8 border border-gray-200">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl text-gray-900">
                    {question.question}
                  </CardTitle>
                  {question.id === "licence_location" && (
                    <CardDescription className="text-sm text-gray-500">
                      Select the type of trade licence your company holds.
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {question.options.map((option) => (
                      <button
                        key={option.value}
                        className="w-full p-4 text-left bg-white hover:bg-gray-50 border border-gray-200 hover:border-blue-300 rounded-lg transition-all duration-200 group"
                        onClick={() => handleAnswer(question.id, option.value)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 group-hover:text-blue-600">
                            {option.label}
                          </span>
                          <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-between items-center py-4">
              <Button
                variant="outline"
                onClick={goBack}
                disabled={currentQuestion === 0}
                className="border-gray-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: questions.length }, (_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i < currentQuestion
                        ? 'bg-blue-600'
                        : i === currentQuestion
                        ? 'bg-blue-400'
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <span className="text-sm text-gray-500">
                {currentQuestion + 1} / {questions.length}
              </span>
            </div>
          </div>
        </div>
      </div>
      <TenantFooter />
    </div>
  );
};

export default DecisionTree;